import { makeStyles, Button } from '@material-ui/core'
import { Tooltip, Zoom } from '@mui/material'
import { useRef, useState } from 'react'
import Upload from 'src/assets/PolicyTile_Images/Upload.svg'
import { UPLOAD_POLICY_DOCUMENT } from 'src/services/individualService'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'
import { showNotificationMessage } from 'src/actions'
import { useMutation } from '@apollo/client'
import { useDispatch } from 'react-redux'
import LoadingModal from 'src/components/common/LoadingModal'
import UploadedDocument from 'src/assets/PolicyTile_Images/UploadedDocument.svg'
import DocumentDeleteIcon from 'src/assets/PolicyTile_Images/DocumentDeleteIcon.svg'
import { NOTIFICATION_ERROR_MESSAGES } from 'src/utils/constants/ValidationErrorMessages'

const RenewUploadModal = ({ policyDetails, closeUploadModal }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [fileUploadError, setFileUploadError] = useState('')
  const fileInputRef = useRef(null)

  const [uploadPolicyDocument, { loading: uploadPolicyDocumentLoading }] =
    useMutation(UPLOAD_POLICY_DOCUMENT(), {
      onError: (error) => {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: error?.graphQLErrors?.[0]?.message,
          })
        )
      },
      onCompleted: () => {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.SUCCESS,
            message: NOTIFICATION_ERROR_MESSAGES?.UPLOAD_SUCCESS,
          })
        )
        setTimeout(() => {
          window?.location?.reload()
        }, 3000)
      },
    })

  const handleFileUpload = (event) => {
    if (event?.target?.files) {
      const file = [event?.target?.files?.[0]]
      setUploadedFiles([...file])
    }
    setFileUploadError('')
  }

  const handleSubmitUploadPolicy = () => {
    if (uploadedFiles?.length === 0) {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.WARNING,
          message: NOTIFICATION_ERROR_MESSAGES?.UPLOAD_WARNING,
        })
      )
      return
    }

    uploadPolicyDocument({
      variables: {
        file: uploadedFiles[0],
        clientId:
          policyDetails?.length >= 0
            ? policyDetails[0]?.clientId
            : policyDetails?.clientId,
        clientType:
          policyDetails?.length >= 0
            ? policyDetails[0]?.policyType
            : policyDetails?.policyType,
        oldPolicyId:
          policyDetails?.length >= 0 ? policyDetails[0]?.id : policyDetails?.id,
      },
    })
    closeUploadModal()
  }

  return (
    <>
      <LoadingModal loading={uploadPolicyDocumentLoading} />
      <div className={classes.mainContainer}>
        <div className={classes.headingText}>
          <span>Please Upload The Policy Document</span>
        </div>
        <div className={classes.fileUploadContainer}>
          {uploadedFiles?.length > 0 ? (
            <div className={classes.filesContainer}>
              <div className={classes.uploadedFile}>
                <img
                  src={UploadedDocument}
                  alt={'UploadDocument'}
                  className={classes.uploadedDocumentImage}
                />
                <div className={classes.uploadedFileName}>
                  <Tooltip
                    TransitionComponent={Zoom}
                    placement='top'
                    title={
                      uploadedFiles?.[0]?.originalName
                        ? uploadedFiles?.[0]?.originalName
                        : uploadedFiles?.[0]?.name
                    }
                    arrow
                  >
                    <div className={classes.uploadFile}>
                      {uploadedFiles?.[0]?.originalName
                        ? uploadedFiles?.[0]?.originalName
                        : uploadedFiles?.[0]?.name}
                    </div>
                  </Tooltip>
                </div>
                <img
                  src={DocumentDeleteIcon}
                  alt={'DocumentDeleteIcon'}
                  className={classes.uploadedDocumentDelete}
                  onClick={() => {
                    setUploadedFiles([])
                  }}
                />
              </div>
            </div>
          ) : (
            <>
              <Button
                variant='outlined'
                component='label'
                className={classes.uploadLogoButton}
                startIcon={
                  <img
                    src={Upload}
                    alt='Upload Icon'
                    className={classes.uploadImage}
                  />
                }
              >
                <span className={classes.inputFileName}>Upload Policy</span>
                <input
                  id='renewalDocumentUpload'
                  type='file'
                  required
                  onChange={handleFileUpload}
                  className={classes.fileUploadButton}
                  ref={fileInputRef}
                />
              </Button>
              <div className={classes.errorText}>{fileUploadError}</div>
            </>
          )}
        </div>
        <div>
          {uploadedFiles?.length > 0 ? (
            <Button
              id='submitButton'
              type='button'
              variant='contained'
              color='secondary'
              className={classes.btnStyles}
              onClick={handleSubmitUploadPolicy}
            >
              Submit
            </Button>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default RenewUploadModal
const useStyles = makeStyles({
  mainContainer: {
    margin: '20px',
    padding: '20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  headingText: {
    fontSize: '14px',
    fontWeight: '400',
    fontFamily: 'roboto',
    lineHeight: '16px',
    color: '#333333',
  },
  filesContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    overflowX: 'scroll',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '4px',
    },
  },
  uploadedFile: {
    minWidth: '100%',
    border: '1px solid #384EB74D',
    borderRadius: '5px',
    padding: '10px',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-around',
    borderStyle: 'dashed',
    background: '#D9D9D940',
  },
  uploadedFileName: {
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700',
    textDecoration: 'underline',
    fontFamily: 'Mulish',
    gap: '10px',
  },
  uploadedDocumentDelete: {
    cursor: 'pointer',
    color: '#E41D1D !important',
    display: 'flex',
    alignItems: 'center',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
  },
  btnStyles: {
    borderRadius: '10px',
    textTransform: 'capitalize',
    background: '#B92250',
    color: '#FFF',
    padding: '8px 95px',
    fontSize: '15px',
    lineHeight: 'unset !important',
    '&:hover': {
      backgroundColor: '#B92250',
    },
  },
  inputFileName: {
    fontSize: '12px',
    fontFamily: 'Mulish',
    fontWeight: '700',
    textTransform: 'capitalize',
    textDecoration: 'underline',
  },
  uploadedDocumentImage: {
    width: '100px',
  },
  uploadLogoButton: {
    borderStyle: 'dashed',
    width: '100%',
    background: '#D9D9D940',
    padding: '25px 10px',
    justifyContent: 'space-around',
  },
  uploadImage: {
    width: '75px',
  },
  uploadFile: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '170px',
  },
  fileUploadButton: {
    display: 'none',
  },
})
